import './UserMetaInfo.scss';
import MicStateIcon from '@component/mic-state';
import PhoneStateIcon from '@component/phone-state';
import Nickname from '@component/nickname';
import Avatar from '@component/avatar';
import VideoState from '@component/video-state';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Input, Modal } from '@tencent/tea-component';
import { useState } from 'react';
import { CaretDownSmallIcon, CloseCircleFilledIcon } from 'tdesign-icons-react';
import HostAudioCtrl from './host-audio-ctrl';
import { UserMetaInfoParams } from './UserMetaInfo.type';
import { AvatarType, RoleType } from '@tencent/wemeet-websdk-next';
import wemeetClient from '@utils/wemeetClient';
import SetCoHost from './set-co-host';
import RetakeCoHost from './retake-co-host';
import SetHost from './set-host';
import RetakeHost from './retake-host';
import HostVideoCtrl from './host-video-ctrl';
import Kickout from './kickout';
import HostShareCtrl from './host-share-ctrl';
import ScreenIcon1x from '@asset/image/user/member_sharing(1).png';
import ScreenIcon2x from '@asset/image/user/member_sharing(1)@2x.png';
import Recoding1x from '@asset/image/user/recording_b.png';
import Recoding2x from '@asset/image/user/recording_b@2x.png';
import { useAppSelector } from '@store/hooks';
import { getMeetingSettings } from '@store/meeting/meeting.slice';
import { openDialog } from '@component/dialog';

/**
 * @description 用户列表中的列表项
 *
 */
export default function UserMetaInfo(props: UserMetaInfoParams) {
  const {
    avatarType = AvatarType.default,
    avatarToken,
    userDistrict,
    nickname = '',
    roleType = RoleType.member,
    isSelf = false,
    isPhoneUser = false,
    phoneOn = false,
    micOn = false,
    videoOn = false,
    recordOn = false,
    shareOn = false,
    selfIsCreator = false,
    selfRoleType = false,
    rtcStream,
  } = props;

  const { t } = useTranslation();
  const meetingSetting = useAppSelector(getMeetingSettings);

  const [selfButtonsShow, setSelfButtonsShow] = useState('hide');
  const [hostButtonsShow, setHostButtonsShow] = useState('hide');
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [modifyNicknameModalVisible, setModifyNicknameModalVisible] = useState(false);
  const [newNickname, setNewNickname] = useState(nickname);

  const selfIsHost = selfRoleType === RoleType.host;
  const selfIsCoHost = selfRoleType === RoleType.coHost;
  const userIsHost = roleType === RoleType.host;
  const userIsCoHost = roleType === RoleType.coHost;

  /** ******************** 以下为交互事件 ************************/

  /** 鼠标进入 */
  const handleMouseEnter = () => {
    if (isSelf) {
      setSelfButtonsShow('show');
    } else if (selfIsHost || (selfIsCoHost && !userIsHost)) {
      setHostButtonsShow('show');
    }
  };
  /** 鼠标离开 */
  const handleMouseLeave = () => {
    setSelfButtonsShow('hide');
    if (!dropDownOpen) setHostButtonsShow('hide');
  };

  /** 展开下拉按钮框 */
  const handleDropDownOpen = (open: boolean) => {
    setDropDownOpen(open);

    if (!open && hostButtonsShow) {
      setHostButtonsShow('hide');
    }
  };

  /** 改名按钮 */
  const handleModifyNicknameBtn = () => {
    if (meetingSetting.allow_renaming === '2') {
      openDialog({
        dialogId: 'renameNotAllowed',
        title: t('提醒'),
        content: t('主持人不允许改名'),
        okText: t('我知道了'),
      });
      return;
    }
    setModifyNicknameModalVisible(true);
  };

  /** 改名弹窗确认按钮 */
  const handleModifyNicknameModalConfirm = () => {
    const self = wemeetClient.userCtrl.getSelfId();
    wemeetClient.userCtrl
      .modifyUserNickname(self, newNickname)
      .then(() => {
        setModifyNicknameModalVisible(false);
      })
      .catch((e) => {
        // todo: 错误上报？
        console.error(e);
      });
  };

  /** 改名弹窗取消按钮 */
  const handleModifyNicknameModalCancel = () => {
    setNewNickname(nickname);
    setModifyNicknameModalVisible(false);
  };

  /** 改名输入框 */
  const handleNewNicknameChange = (value: string) => {
    setNewNickname(value);
  };

  /** 改名输入框删除按钮 */
  const handleDeleteNewNickname = () => {
    setNewNickname('');
  };

  /** ******************** 以下为逻辑处理 ************************/

  /**
   * @description 拼接身份标识文案
   * */
  const generateIdentifier = (roleType: RoleType, isSelf: boolean) => {
    let identifier = '';
    switch (roleType) {
      case RoleType.host:
        identifier += t('主持人');
        break;
      case RoleType.coHost:
        identifier += t('联席主持人');
        break;
      default:
        break;
    }
    if (isSelf) {
      if (identifier.length > 0) {
        identifier += ',';
      }
      identifier += t('我');
    }

    if (identifier.length > 0) {
      identifier = `(${identifier})`;
    }

    return identifier;
  };

  /** 对成员的操作按钮按显示条件过滤 */
  const morePopButtons = [
    {
      cpn: <SetCoHost user={props} key="SetCoHost" />,
      filter: selfIsHost && !isSelf && !userIsCoHost && !isPhoneUser,
    },
    { cpn: <RetakeCoHost user={props} key="RetakeCoHost" />, filter: selfIsHost && userIsCoHost },
    {
      cpn: <SetHost user={props} key="SetHost" />,
      filter: selfIsHost && !isSelf && !isPhoneUser,
    },
    {
      cpn: <RetakeHost user={props} key="RetakeHost" />,
      filter: selfIsCreator && !isSelf && userIsHost,
    },
    {
      cpn: <HostVideoCtrl user={props} key="HostVideoCtrl" />,
      filter: (selfIsHost || selfIsCoHost) && !isSelf && videoOn,
    },
    {
      cpn: <HostShareCtrl user={props} key="HostShareCtrl" />,
      filter: (selfIsHost || selfIsCoHost) && !isSelf && shareOn,
    },
    {
      cpn: <Kickout user={props} key="Kickout" />,
      filter: (selfIsHost || selfIsCoHost) && !isSelf && !userIsHost && !userIsCoHost,
    },
  ].filter(({ filter }) => !!filter);

  return (
    <div
      className={'user-meta-info'}
      // onClick={handleMouseEnter}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={'user-meta-info__avatar-wrap'}>
        <Avatar
          avatarType={avatarType}
          avatarToken={avatarToken}
          userDistrict={userDistrict}
          nickname={nickname}
          mode={'small'}
        />
      </div>
      <div className={'user-meta-info__nickname-wrap'}>
        <Nickname nickname={nickname} mode={'large'} />
        <span className={'identifier'}>{generateIdentifier(roleType, isSelf)}</span>
      </div>
      <div className="user-meta-info__icons">
        {shareOn && (
          <img srcSet={`${ScreenIcon1x}  1x,${ScreenIcon2x} 2x`} alt="" className="user-meta-info__icons__item" />
        )}
        {recordOn && (
          <img srcSet={`${Recoding1x}  1x,${Recoding2x} 2x`} alt="" className="user-meta-info__icons__item" />
        )}
        {isPhoneUser ? (
          <PhoneStateIcon mode={'blue'} on={phoneOn} stream={rtcStream?.stream} />
        ) : (
          <MicStateIcon micOn={micOn} stream={rtcStream?.stream} />
        )}
        <VideoState on={videoOn} mode={'small'} />
      </div>
      <div className={`user-meta-info__buttons-self user-meta-info__buttons-self--${selfButtonsShow}`}>
        <HostAudioCtrl user={props} isMute={micOn || phoneOn} isMuteSelf={true} />
        <Button type={'primary'} onClick={handleModifyNicknameBtn}>
          {t('改名')}
        </Button>
      </div>
      <div className={`user-meta-info__buttons-host user-meta-info__buttons-host--${hostButtonsShow}`}>
        <HostAudioCtrl user={props} isMute={micOn || phoneOn} isMuteSelf={false} />
        <Dropdown
          overlayClassName={'user-meta-info__dropdown'}
          button={
            <Button className={'user-meta-info__buttons-host__more'}>
              {t('更多')}
              <CaretDownSmallIcon />
            </Button>
          }
          trigger={'click'}
          placement={'bottom-end'}
          appearance={'pure'}
          clickClose={true}
          onOpenChange={handleDropDownOpen}
        >
          <ul className={'user-meta-info__dropdown__list'}>{morePopButtons.map((item) => item.cpn)}</ul>
        </Dropdown>
      </div>
      <Modal
        className={'user-meta-info__modify-nickname-modal'}
        visible={modifyNicknameModalVisible}
        caption={t('修改昵称')}
        disableCloseIcon
      >
        <Modal.Body>
          <div className="user-meta-info__modify-nickname-modal__content">
            <Input value={newNickname} placeholder={t('请输入名称')} onChange={handleNewNicknameChange} />
            <CloseCircleFilledIcon
              className={`close-icon ${newNickname.length ? '' : 'hide'}`}
              size={'14px'}
              onClick={handleDeleteNewNickname}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="weak" onClick={handleModifyNicknameModalCancel}>
            {t('取消')}
          </Button>
          <Button type="primary" disabled={newNickname.length === 0} onClick={handleModifyNicknameModalConfirm}>
            {t('确定')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
